import * as DropdownMenu from '@radix-ui/react-dropdown-menu'
import { CheckIcon, MoonIcon, SunIcon, SystemThemeIcon } from '@/components/icons'
import { useTheme } from '@/stores/ThemeProvider'
import type { ThemePreference } from '@/types/ui'
import { cn } from '@/utils/cn'

/**
 * 主题切换（AGENTS.md 4.2 要求可手动切换）。
 *
 * 设计稿没有画这个控件 —— 它也没画深色主题的任何一页，但 AGENTS.md 明确要求
 * 实现深色主题并支持手动切换，所以需要一个入口。放在顶栏用户头像左侧，
 * 是站点类产品的惯例位置，不会打乱设计稿的其余布局。
 *
 * 用下拉菜单而不是单个切换按钮，是为了让 `system`（跟随系统）可选：
 * 两态按钮无法表达「跟随系统」，用户想回到自动就只能清缓存。
 */

const OPTIONS: Array<{ value: ThemePreference; label: string; Icon: typeof SunIcon }> = [
  { value: 'light', label: '浅色', Icon: SunIcon },
  { value: 'dark', label: '深色', Icon: MoonIcon },
  { value: 'system', label: '跟随系统', Icon: SystemThemeIcon },
]

export function ThemeToggle({ className }: { className?: string }) {
  const { preference, resolved, setPreference } = useTheme()

  return (
    <DropdownMenu.Root>
      <DropdownMenu.Trigger
        aria-label={`切换主题（当前：${OPTIONS.find((o) => o.value === preference)?.label}）`}
        className={cn(
          'inline-flex size-10 shrink-0 items-center justify-center rounded-card text-muted-foreground transition-colors hover:bg-surface-muted hover:text-foreground',
          className,
        )}
      >
        {/* 图标反映**实际生效**的主题，而不是 preference —— 用户关心的是眼前是什么。 */}
        {resolved === 'dark' ? <MoonIcon className="size-5" /> : <SunIcon className="size-5" />}
      </DropdownMenu.Trigger>

      <DropdownMenu.Portal>
        <DropdownMenu.Content
          align="end"
          sideOffset={8}
          className="z-50 min-w-40 animate-slide-up overflow-hidden rounded-card border border-border bg-surface-raised p-1 shadow-overlay"
        >
          {OPTIONS.map(({ value, label, Icon }) => (
            <DropdownMenu.Item
              key={value}
              onSelect={() => setPreference(value)}
              className="flex cursor-pointer items-center gap-2.5 rounded-lg px-2.5 py-2 text-sm text-foreground outline-none transition-colors data-[highlighted]:bg-surface-muted"
            >
              <Icon className="size-4 text-muted-foreground" />
              <span className="flex-1">{label}</span>
              {preference === value && <CheckIcon className="size-4 text-primary" />}
            </DropdownMenu.Item>
          ))}
        </DropdownMenu.Content>
      </DropdownMenu.Portal>
    </DropdownMenu.Root>
  )
}
